import { toast as sonner } from 'sonner';

/**
 * Toast facade — a direct port of `core/services/notification.service.ts`.
 *
 * Method names are kept identical so Angular call sites port by find-and-replace
 * (`notificationService.success(...)` → `notify.success(...)`).
 *
 * `handleError` is DELIBERATELY NOT EXPOSED. In the Angular app both the HTTP
 * interceptor and `ApiBaseService.handleError` toasted the same failure, so many
 * errors produced two snackbars. The axios response interceptor is now the single
 * error-toast layer; re-exposing a component-callable `handleError` would recreate
 * the bug. Call sites that need a custom message pass `meta.fallbackMessage` on the
 * request instead.
 *
 * Standard notifications remain visible for 5 seconds. Errors remain visible for
 * 15 seconds so users have enough time to read and act on them. `loading` has no
 * timeout (Angular used `duration: 0`).
 */
/**
 * ============================================================================
 * One message per line, when the server sends a LIST as one string.
 * ============================================================================
 *
 * A 422 arrives as a single `message`, with the individual field problems joined by `"; "`:
 *
 *     "Building Number must be at most 10 characters; Postal Code must be at most 10 characters"
 *
 * Rendered as one paragraph that wraps mid-sentence, it reads as prose and a user has to parse it
 * to find out that TWO fields are wrong. Splitting on the separator the server already uses turns
 * it back into the list it was before being flattened.
 *
 * ⚠️ `"; "` WITH THE SPACE, never a bare semicolon. The space is what makes this a separator
 * between clauses rather than punctuation inside one, and matching the bare character would break
 * any message that legitimately contains one.
 *
 * The trailing separator case is real: some responses end the list with one, which would otherwise
 * leave a blank final line.
 */
export function splitMessageLines(message: string): string {
  return message
    .split('; ')
    .map((line) => line.trim())
    .filter(Boolean)
    .join('\n');
}

const STANDARD_TOAST_DURATION_MS = 5000;
const ERROR_TOAST_DURATION_MS = 15000;

export const notify = {
  success: (message: string) =>
    sonner.success(message, { duration: STANDARD_TOAST_DURATION_MS }),
  /*
   * The one channel that splits. Validation lists arrive here and nowhere else — a success or an
   * info message is written as prose, and breaking it on a semicolon would be a change for the
   * worse. The Toaster renders newlines for every channel; only this one produces them.
   */
  error: (message: string) =>
    sonner.error(splitMessageLines(message), { duration: ERROR_TOAST_DURATION_MS }),
  warning: (message: string) =>
    sonner.warning(message, { duration: STANDARD_TOAST_DURATION_MS }),
  info: (message: string) => sonner.info(message, { duration: STANDARD_TOAST_DURATION_MS }),
  /** Returns an id; pass it to `dismiss` to cancel. Mirrors MatSnackBarRef. */
  loading: (message = 'Loading...') => sonner.loading(message, { duration: Infinity }),
  dismiss: (id?: string | number) => sonner.dismiss(id),
} as const;
